import { createTheme, alpha, type PaletteMode, type SxProps, type Theme } from '@mui/material/styles';
import {
  TooltipHoverEnterDelayMs,
  TooltipHoverLeaveDelayMs,
} from '../shared/ui/common/tooltipTiming';

export { alpha } from '@mui/material/styles';

// ============================================================================
// DESIGN TOKENS (exported for direct component use)
// ============================================================================

/**
 * Spacing scale using 8px base unit
 * Use these constants for consistent spacing across components
 */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

/**
 * MUI spacing multipliers for `sx` (gap, Stack `spacing`, padding, margin — multiplied by theme spacing unit, default 8px).
 * Use instead of ad-hoc `gap` / `Stack spacing` values when you want the shared 2px–16px (and 12/18px section) scale.
 */
export const layoutGap = {
  /** ~0.4px — collapsed stacks (labels barely separated) */
  nano: 0.05,
  /** ~0.8px — hairline vertical rhythm */
  fine: 0.1,
  /** 1px — half-step toolbar clusters */
  mini: 0.125,
  /** ~1.2px — dock / minimap stacks */
  tight: 0.15,
  /** ~1.44px — dense nested controls */
  shim: 0.18,
  /** ~1.6px — chip / badge rows */
  tick: 0.2,
  /** 2px — minimal stack spacing (tight label rows) */
  micro: 0.25,
  /** ~2.24px — utility chrome icon strips */
  trim: 0.28,
  /** ~2.4px — compact lists */
  note: 0.3,
  /** ~2.8px — dense caption/list stacks */
  snug: 0.35,
  /** ~3.2px — inline chip/icon rows */
  flush: 0.4,
  /** ~3.6px — nested card rows */
  cozy: 0.45,
  /** 4px — icon + text, chips */
  dense: 0.5,
  /** ~4.4px — chip/tool rows */
  blend: 0.55,
  /** ~4.8px — dense multi-column tool rows */
  steady: 0.6,
  /** ~5.2px — sub-rows approaching toolbar comfort */
  loft: 0.65,
  /** ~5.6px — split headers / wide tool rows */
  flow: 0.7,
  /** 6px — toolbars and comfortable inline rows */
  comfort: 0.75,
  /** ~6.4px — section summaries */
  rise: 0.8,
  /** ~6.8px — card rails */
  plate: 0.85,
  /** ~7.2px — near full row rhythm */
  slant: 0.9,
  /** 8px — stacked fields, default vertical rhythm */
  row: 1,
  /** ~8.4px — between row and section */
  span: 1.05,
  /** ~8.8px — wide dialog form rows */
  bump: 1.1,
  /** ~9.6px — wide split layouts */
  ledge: 1.2,
  /** 10px — filter panels / dense dialogs */
  rack: 1.25,
  /** ~11.2px — auth / hero stacks */
  shell: 1.4,
  /** 12px — sections and wizard separation */
  section: 1.5,
  /** 16px — dialogs / emphasis stacks */
  block: 2,
  /** ~17.6px — generous screen-center stacks */
  tower: 2.2,
} as const;

/**
 * Border radius values for different UI elements
 */
export const borderRadius = {
  none: 0,
  sm: 4,        // Small elements (chips, small buttons)
  md: 8,        // Default (cards, buttons, inputs)
  lg: 12,       // Large cards, dialogs
  xl: 16,       // Extra large surfaces
  circle: '50%', // Circular elements (avatars, icon containers)
  full: 9999,   // Pill-shaped elements
} as const;

/**
 * Focus ring configuration for consistent keyboard navigation indicators
 * Applied to all interactive elements for WCAG 2.1 AA compliance
 */
export const focusRing = {
  width: 2,
  offset: 2,
  style: 'solid',
} as const;

/**
 * Standard visible border for app controls.
 * Active/hover state should be expressed with fill, text, or icon color rather than changing the outer outline.
 */
export const getControlBorderColor = (theme: Theme) =>
  // Dark-mode alpha must stay >= ~0.45: at 150% display scaling a 1px border
  // at a fractional position splits across two device-pixel rows, and below
  // ~0.45 each row falls under the visibility threshold (fields lost their
  // bottom outline depending on scroll/layout phase).
  alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.64 : 0.45);

export const getControlBorder = (theme: Theme) => `1px solid ${getControlBorderColor(theme)}`;

/**
 * Standard visible border for form fields.
 * Error/invalid states stay semantic; normal hover and focus states keep the app-wide neutral outline.
 */
export const getFieldBorderColor = (theme: Theme) => getControlBorderColor(theme);

export const getFieldBorder = (theme: Theme) => `1px solid ${getFieldBorderColor(theme)}`;

/**
 * Standard visible border for generic elevated surfaces.
 * Semantic cards may still use status colors; neutral containers should use this.
 */
export const getSurfaceBorderColor = (theme: Theme) => getControlBorderColor(theme);

export const getSurfaceBorder = (theme: Theme) => `1px solid ${getSurfaceBorderColor(theme)}`;

/**
 * Common layout dimensions for consistent sizing
 * Use these instead of hardcoded pixel values
 */
export const layout = {
  scrollbarWidth: 6,        // Custom scrollbar width
  minButtonWidth: 100,      // Minimum interactive button width
  minTouchTarget: 44,       // Minimum touch target size (mobile)
  inputPaddingX: 10,        // Horizontal padding for inputs
  inputPaddingY: 8,         // Vertical padding for inputs
  avatarSmall: 120,         // Small avatar/icon size
  avatarMedium: 180,        // Medium avatar/icon size
  accordionMinHeight: 40,   // Minimum height for accordion headers
} as const;

export const componentSizes = {
  toolbarCompact: 36,
  toolbar: 40,
  toolbarLarge: 44,
  treeItem: 36,
  tableRow: 40,
  tableRowCompact: 32,
  button: 32,
  buttonSm: 28,
  input: 32,
  inputSm: 28,
  chip: 24,
  chipSm: 20,
  iconButton: 32,
  iconButtonSm: 24,
  treeIcon: 16,
  icon: 20,
} as const;

export const workspaceLayout = {
  treeWidth: 220,
  treeWidthCollapsed: 48,
  contentMinWidth: 400,
  modalMaxWidth: 480,
} as const;

export const quickFillLayout = {
  sidePanelWidth: 304,
  propertySpotlightWidth: 340,
  valueSpotlightWidth: 344,
} as const;

export interface SemanticSurfaceTokens {
  background: {
    panel: string;
    card: string;
    elevated: string;
    hover: string;
    selected: string;
    active: string;
  };
  border: {
    subtle: string;
    light: string;
    default: string;
    strong: string;
    active: string;
  };
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    disabled: string;
  };
  status: {
    success: { bg: string; text: string; border: string };
    warning: { bg: string; text: string; border: string };
    error: { bg: string; text: string; border: string };
    info: { bg: string; text: string; border: string };
    neutral: { bg: string; text: string; border: string };
  };
}

export const getSemanticSurfaceTokens = (theme: Theme): SemanticSurfaceTokens => ({
  background: {
    panel: alpha(theme.palette.background.paper, 0.04),
    card: alpha(theme.palette.background.paper, 0.06),
    elevated: alpha(theme.palette.background.paper, 0.08),
    hover: alpha(theme.palette.primary.main, 0.04),
    selected: alpha(theme.palette.primary.main, 0.08),
    active: alpha(theme.palette.primary.main, 0.12),
  },
  border: {
    subtle: alpha(theme.palette.divider, 0.04),
    light: alpha(theme.palette.divider, 0.06),
    default: alpha(theme.palette.divider, 0.08),
    strong: alpha(theme.palette.divider, 0.12),
    active: alpha(theme.palette.primary.main, 0.24),
  },
  text: {
    primary: theme.palette.text.primary,
    secondary: alpha(theme.palette.text.secondary, 0.85),
    tertiary: alpha(theme.palette.text.secondary, 0.6),
    disabled: alpha(theme.palette.text.secondary, 0.38),
  },
  status: {
    success: {
      bg: alpha(theme.palette.success.main, 0.12),
      text: theme.palette.success.light,
      border: alpha(theme.palette.success.main, 0.2),
    },
    warning: {
      bg: alpha(theme.palette.warning.main, 0.14),
      text: theme.palette.warning.light,
      border: alpha(theme.palette.warning.main, 0.22),
    },
    error: {
      bg: alpha(theme.palette.error.main, 0.12),
      text: theme.palette.error.light,
      border: alpha(theme.palette.error.main, 0.2),
    },
    info: {
      bg: alpha(theme.palette.info.main, 0.12),
      text: theme.palette.info.light,
      border: alpha(theme.palette.info.main, 0.2),
    },
    neutral: {
      bg: alpha(theme.palette.background.default, 0.3),
      text: alpha(theme.palette.text.secondary, 0.8),
      border: alpha(theme.palette.divider, 0.1),
    },
  },
});

// ============================================================================
// ACCESSIBLE COLOR PALETTE (WCAG 2.1 AA Compliant)
// ============================================================================

const sharedGreyPalette = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
} as const;

// Define accessible dark color palette
const darkAccessiblePalette = {
  // Primary purple remains but adjusted for accessibility
  primary: {
    main: '#bb6bd9', // Lightened to ensure contrast
    light: '#d08ee8',
    dark: '#9e4cc5', // Original purple as dark variant
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#8c43ba',
    light: '#b875e0',
    dark: '#622e82',
    contrastText: '#ffffff',
  },
  // Enhanced contrast text colors (WCAG AA compliant)
  text: {
    primary: '#ffffff', // 21:1 contrast on #1a1a1a
    secondary: '#b3b3b3', // 7.6:1 contrast on #1a1a1a
    disabled: '#949494', // 4.5:1 contrast on #1a1a1a (WCAG AA minimum)
  },
  // Background colors with better contrast
  background: {
    default: '#0f0f0f', // Darker background for better contrast
    paper: '#1a1a1a',
  },
  // Enhanced error, warning, info, success colors for better contrast
  error: {
    main: '#f88070', // Lighter red for better contrast
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ffb74d', // Lighter orange for better contrast
    contrastText: '#000000',
  },
  info: {
    main: '#64b5f6', // Lighter blue for better contrast
    contrastText: '#000000',
  },
  success: {
    main: '#81c784', // Lighter green for better contrast
    contrastText: '#000000',
  },
  // Additional contrast-focused colors
  grey: sharedGreyPalette,
  divider: 'rgba(255, 255, 255, 0.12)', // Better contrast divider
  // Custom action colors for UI states
  action: {
    active: '#ffffff',
    hover: 'rgba(255, 255, 255, 0.08)',
    hoverOpacity: 0.08,
    selected: 'rgba(187, 107, 217, 0.16)',
    selectedOpacity: 0.16,
    disabled: '#949494',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(187, 107, 217, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.24,
  },
};

const lightAccessiblePalette = {
  primary: {
    main: '#6f2da8',
    light: '#8c43ba',
    dark: '#4f1f78',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#006d77',
    light: '#168891',
    dark: '#004f56',
    contrastText: '#ffffff',
  },
  text: {
    primary: '#172033',
    secondary: '#4f5b6b',
    disabled: '#6f7784',
  },
  background: {
    default: '#f6f8fb',
    paper: '#ffffff',
  },
  error: {
    main: '#b42318',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#8a4b00',
    contrastText: '#ffffff',
  },
  info: {
    main: '#005a9e',
    contrastText: '#ffffff',
  },
  success: {
    main: '#1b6b3a',
    contrastText: '#ffffff',
  },
  grey: sharedGreyPalette,
  divider: 'rgba(23, 32, 51, 0.16)',
  action: {
    active: '#172033',
    hover: 'rgba(23, 32, 51, 0.06)',
    hoverOpacity: 0.06,
    selected: 'rgba(111, 45, 168, 0.12)',
    selectedOpacity: 0.12,
    disabled: '#6f7784',
    disabledBackground: 'rgba(23, 32, 51, 0.08)',
    disabledOpacity: 0.38,
    focus: 'rgba(111, 45, 168, 0.14)',
    focusOpacity: 0.14,
    activatedOpacity: 0.2,
  },
};

// Create the accessible theme
export const createPipeexTheme = (mode: PaletteMode = 'dark') => {
  const accessiblePalette = mode === 'light' ? lightAccessiblePalette : darkAccessiblePalette;
  const overlayColor = mode === 'light' ? '#000000' : '#ffffff';
  const elevatedShadow = mode === 'light' ? alpha('#273349', 0.14) : alpha('#000000', 0.38);
  const controlBorderColor = alpha(accessiblePalette.divider, mode === 'light' ? 0.64 : 0.45);
  const controlBorder = `1px solid ${controlBorderColor}`;
  const fieldBorderColor = controlBorderColor;
  const surfaceBorderColor = controlBorderColor;
  const autocompleteIndicatorButtonStyles = {
    width: 22,
    height: 22,
    minWidth: 22,
    minHeight: 22,
    padding: 0,
    margin: '0 !important',
    marginRight: '0 !important',
    border: '1px solid transparent',
    borderRadius: '50%',
    color: alpha(accessiblePalette.text.secondary, mode === 'dark' ? 0.78 : 0.72),
    backgroundColor: 'transparent',
    '&:hover': {
      color: accessiblePalette.text.primary,
      backgroundColor: alpha(accessiblePalette.text.primary, mode === 'dark' ? 0.12 : 0.08),
      borderColor: 'transparent',
    },
    '&:focus-visible': {
      outline: `${focusRing.width}px ${focusRing.style} ${accessiblePalette.primary.main}`,
      outlineOffset: `${focusRing.offset}px`,
      color: accessiblePalette.text.primary,
      backgroundColor: alpha(accessiblePalette.primary.main, mode === 'dark' ? 0.18 : 0.12),
      borderColor: 'transparent',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
  };

  return createTheme({
  palette: {
    mode,
    ...accessiblePalette,
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: accessiblePalette.text.primary,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.25,
      color: accessiblePalette.text.primary,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: accessiblePalette.text.primary,
    },
    h4: {
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: 1.35,
      color: accessiblePalette.text.primary,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: accessiblePalette.text.primary,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.45,
      color: accessiblePalette.text.primary,
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: accessiblePalette.text.primary,
    },
    subtitle2: {
      fontSize: '0.8125rem',
      fontWeight: 500,
      lineHeight: 1.57,
      color: accessiblePalette.text.primary,
    },
    body1: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
      color: accessiblePalette.text.primary,
    },
    body2: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: accessiblePalette.text.secondary,
    },
    caption: {
      fontSize: '0.6875rem',
      lineHeight: 1.5,
      color: accessiblePalette.text.secondary,
    },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.08em',
      color: accessiblePalette.text.secondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.8125rem',
    },
  },
  spacing: 8, // Base spacing unit
  shape: {
    borderRadius: 8, // Consistent border radius
  },
  // Z-index layering strategy to prevent overlap issues
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Ensure focus indicators are visible for keyboard navigation
        '*:focus-visible': {
          outline: `${focusRing.width}px ${focusRing.style} ${accessiblePalette.primary.main}`,
          outlineOffset: `${focusRing.offset}px`,
        },
        // Remove default outline when not needed
        '*:focus': {
          outline: 'none',
        },
        // Fix for section > fieldset white border issue
        'section > fieldset': {
          border: 'none',
          borderColor: 'transparent',
        },
        'fieldset': {
          borderColor: alpha(overlayColor, 0.2),
        },
        // Global scrollbar styling
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          background: alpha(accessiblePalette.primary.main, 0.2),
          borderRadius: '4px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: alpha(accessiblePalette.primary.main, 0.4),
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default MUI background image
          backgroundColor: accessiblePalette.background.paper,
          color: accessiblePalette.text.primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: `${borderRadius.md}px`,
          fontWeight: 600,
          fontSize: '0.8125rem',
          padding: '5px 14px',
          minHeight: '32px',
          lineHeight: 1.2,
          boxShadow: 'none',
          border: controlBorder,
          transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
          '&:hover': {
            borderColor: controlBorderColor,
          },
          '&:focus-visible': {
            outline: `${focusRing.width}px ${focusRing.style} ${accessiblePalette.primary.main}`,
            outlineOffset: `${focusRing.offset}px`,
            borderColor: controlBorderColor,
          },
          '&:disabled': {
            borderColor: controlBorderColor,
          },
        },
        contained: {
          color: accessiblePalette.primary.contrastText,
          backgroundColor: accessiblePalette.primary.main,
          borderColor: controlBorderColor,
          '&:hover': {
            backgroundColor: accessiblePalette.primary.dark,
            borderColor: controlBorderColor,
          },
          '&:focus-visible': {
            backgroundColor: accessiblePalette.primary.dark,
            borderColor: controlBorderColor,
          },
          '&:disabled': {
            color: accessiblePalette.grey[500],
            backgroundColor: accessiblePalette.grey[800],
            borderColor: controlBorderColor,
          },
        },
        outlined: {
          color: accessiblePalette.text.primary,
          borderColor: controlBorderColor,
          backgroundColor: alpha(accessiblePalette.background.paper, 0.18),
          '&:hover': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.08),
            borderColor: controlBorderColor,
          },
          '&:focus-visible': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.12),
            borderColor: controlBorderColor,
          },
          '&:disabled': {
            color: accessiblePalette.text.disabled,
            borderColor: controlBorderColor,
          },
        },
        text: {
          color: accessiblePalette.text.primary,
          borderColor: controlBorderColor,
          '&:hover': {
            backgroundColor: alpha(overlayColor, 0.06),
            borderColor: controlBorderColor,
          },
          '&:focus-visible': {
            backgroundColor: alpha(overlayColor, 0.12),
            borderColor: controlBorderColor,
          },
          '&:disabled': {
            color: accessiblePalette.text.disabled,
            borderColor: controlBorderColor,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-error fieldset': {
              borderColor: accessiblePalette.error.main,
            },
            color: accessiblePalette.text.primary,
            backgroundColor: alpha(overlayColor, 0.05),
          },
          '& .MuiInputBase-input': {
            padding: '12px 14px',
            fontSize: '0.8125rem',
            // One selection color app-wide: the OS default blue clashes with the
            // dark palette and reads as a stray highlight next to themed fields.
            '::selection': {
              backgroundColor: alpha(accessiblePalette.primary.main, 0.35),
              color: accessiblePalette.text.primary,
            },
          },
          '& .MuiInputLabel-root': {
            color: accessiblePalette.text.secondary,
            '&.Mui-focused': {
              color: accessiblePalette.primary.main,
            },
            '&.Mui-error': {
              color: accessiblePalette.error.main,
            },
          },
          '& .MuiFormHelperText-root': {
            color: accessiblePalette.text.secondary,
            '&.Mui-error': {
              color: accessiblePalette.error.main,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: alpha(overlayColor, 0.05),
          },
          color: accessiblePalette.text.primary,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
            borderWidth: 1,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
          },
        },
        notchedOutline: {
          borderColor: fieldBorderColor,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: accessiblePalette.text.primary,
          '&.Mui-selected': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.16),
            '&:hover': {
              backgroundColor: alpha(accessiblePalette.primary.main, 0.24),
            },
            '&.Mui-focusVisible': {
              backgroundColor: alpha(accessiblePalette.primary.main, 0.24),
            },
          },
          '&:hover': {
            backgroundColor: alpha(overlayColor, 0.08),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(overlayColor, 0.12),
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: accessiblePalette.grey[500], // Better contrast for unchecked state
          '&.Mui-checked': {
            color: accessiblePalette.primary.main, // Accessible checked state
          },
          '&.Mui-disabled': {
            color: accessiblePalette.text.disabled, // Better contrast for disabled state
          },
          '&:hover': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.12),
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: accessiblePalette.text.primary,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: TooltipHoverEnterDelayMs,
        enterNextDelay: TooltipHoverEnterDelayMs,
        leaveDelay: TooltipHoverLeaveDelayMs,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(accessiblePalette.grey[900], 0.96),
          color: accessiblePalette.text.primary,
          fontSize: '0.76rem',
          lineHeight: 1.55,
          padding: '12px 14px',
          maxWidth: 360,
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${surfaceBorderColor}`,
          boxShadow: `0 16px 32px ${elevatedShadow}`,
          backdropFilter: 'blur(8px)',
        },
        arrow: {
          color: alpha(accessiblePalette.grey[900], 0.96),
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${accessiblePalette.divider}`,
          padding: '10px 16px',
          color: accessiblePalette.text.primary,
          fontSize: '0.8125rem',
        },
        head: {
          color: accessiblePalette.text.primary,
          fontWeight: 600,
          backgroundColor: accessiblePalette.grey[900],
          fontSize: '0.75rem',
        },
        body: {
          color: accessiblePalette.text.primary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: accessiblePalette.primary.main,
          '&:hover': {
            color: accessiblePalette.primary.light,
          },
          '&:focus-visible': {
            outline: `${focusRing.width}px ${focusRing.style} ${accessiblePalette.primary.main}`,
            outlineOffset: `${focusRing.offset}px`,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: accessiblePalette.grey[500],
          '&.Mui-checked': {
            color: accessiblePalette.primary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: accessiblePalette.primary.main,
              opacity: 0.5,
            },
          },
          '&.Mui-focusVisible': {
            '& + .MuiSwitch-track': {
              boxShadow: `0 0 0 2px ${accessiblePalette.background.paper}, 0 0 0 4px ${accessiblePalette.primary.main}`,
            },
          },
        },
        track: {
          backgroundColor: accessiblePalette.grey[600],
          opacity: 1,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: `${borderRadius.sm}px`,
          border: controlBorder,
          color: accessiblePalette.text.primary,
          '&:hover': {
            backgroundColor: alpha(overlayColor, 0.08),
            borderColor: controlBorderColor,
          },
          '&:focus-visible': {
            outline: `${focusRing.width}px ${focusRing.style} ${accessiblePalette.primary.main}`,
            outlineOffset: `${focusRing.offset}px`,
            backgroundColor: alpha(accessiblePalette.primary.main, 0.12),
            borderColor: controlBorderColor,
          },
          '&:disabled': {
            color: accessiblePalette.text.disabled,
            borderColor: controlBorderColor,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        endAdornment: {
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        },
        clearIndicator: autocompleteIndicatorButtonStyles,
        popupIndicator: autocompleteIndicatorButtonStyles,
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          minHeight: '32px',
          borderRadius: `${borderRadius.md}px`,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          borderColor: controlBorderColor,
          color: accessiblePalette.text.secondary,
          '&:hover': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.06),
            borderColor: controlBorderColor,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(accessiblePalette.primary.main, 0.14),
            color: accessiblePalette.primary.light,
            borderColor: controlBorderColor,
            '&:hover': {
              backgroundColor: alpha(accessiblePalette.primary.main, 0.18),
              borderColor: controlBorderColor,
            },
          },
          '&.Mui-disabled': {
            borderColor: controlBorderColor,
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        grouped: {
          minHeight: '32px',
          borderRadius: `${borderRadius.md}px`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: accessiblePalette.background.paper,
          '&:before': {
            display: 'none', // Remove default top border
          },
          '&:focus-within': {
            boxShadow: `0 0 0 ${focusRing.width}px ${accessiblePalette.primary.main}`,
          },
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: `${focusRing.width}px ${focusRing.style} ${accessiblePalette.primary.main}`,
            outlineOffset: `${focusRing.offset}px`,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root.Mui-focused': {
            color: accessiblePalette.primary.main,
          },
          '& .MuiFormLabel-root.Mui-error': {
            color: accessiblePalette.error.main,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: accessiblePalette.text.secondary,
          '&.Mui-focused': {
            color: accessiblePalette.primary.main,
          },
          '&.Mui-error': {
            color: accessiblePalette.error.main,
          },
          '&.MuiInputLabel-shrink': {
            backgroundColor: accessiblePalette.background.paper,
            paddingLeft: 4,
            paddingRight: 4,
          },
        },
      },
    },
  },
  });
};

export const accessibleTheme = createPipeexTheme('dark');

// ============================================================================
// COMMON STYLE UTILITIES (for consistent component styling)
// ============================================================================

/**
 * Common glassmorphism card style used across the application
 */
export const getGlassCard = (theme: Theme) => ({
  backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.92 : 0.6),
  backdropFilter: 'blur(12px)',
  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.8 : 0.3)}`,
  borderRadius: `${borderRadius.lg}px`,
  boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, theme.palette.mode === 'light' ? 0.08 : 0.1)}`,
});

/**
 * Full-screen PipeEX surface. The native WebView2 window owns the visible frame.
 */
export const getUnifiedScreenFrame = (theme: Theme) => ({
  backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.98 : 0.84),
  border: 0,
  borderRadius: 0,
  boxShadow: 'none',
});

/**
 * Modern button styles
 */
export const getModernButton = (theme: Theme) => ({
  contained: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: `${borderRadius.md}px`,
    px: 2.25,
    py: 0.75,
    minHeight: 32,
    boxShadow: 'none',
    border: getControlBorder(theme),
    '&:hover': {
      boxShadow: 'none',
      borderColor: getControlBorderColor(theme),
    },
    transition: 'all 0.2s ease',
  },
  outlined: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: `${borderRadius.md}px`,
    minHeight: 32,
    borderColor: getControlBorderColor(theme),
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.82 : 0.18),
    '&:hover': {
      borderColor: getControlBorderColor(theme),
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    transition: 'all 0.2s ease',
  },
});

/**
 * Table styles
 */
export const getModernTable = (theme: Theme) => ({
  container: {
    borderRadius: `${borderRadius.lg}px`,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.85 : 0.3)}`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.98 : 0.4),
    overflow: 'hidden',
  },
  header: {
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 1 : 0.95),
    borderBottom: `2px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.9 : 0.5)}`,
    fontWeight: 700,
    fontSize: '0.75rem',
  },
  filterRow: {
    backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.85 : 0.5),
    borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.8 : 0.5)}`,
  },
  selectedRow: {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
});

/**
 * Scrollbar styles
 */
export const getModernScrollbar = (theme: Theme) => ({
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.28 : 0.2),
    borderRadius: '4px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.4),
    },
  },
});

/**
 * Thin scrollbar styles (for compact areas)
 * @param variant - 'primary' keeps the app-wide primary-colored thumb; 'divider' uses subtle divider colors closer to legacy mapping scrollbars.
 */
export const getModernScrollbarThin = (theme: Theme, variant: 'primary' | 'divider' = 'primary') => {
  const thumb = variant === 'divider'
    ? alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.24 : 0.12)
    : alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.28 : 0.2);
  const thumbHover = variant === 'divider'
    ? alpha(theme.palette.primary.main, 0.24)
    : alpha(theme.palette.primary.main, 0.4);

  return {
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: thumb,
      borderRadius: '3px',
      '&:hover': {
        background: thumbHover,
      },
    },
  };
};

/**
 * Form field styles
 */
export const getModernFormField = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: `${borderRadius.md}px`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.98 : 0.6),
    transition: 'all 0.2s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: getFieldBorderColor(theme),
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 1 : 0.8),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: getFieldBorderColor(theme),
      },
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 1 : 0.9),
      boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.22 : 0.18)}`,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: getFieldBorderColor(theme),
        borderWidth: 1,
      },
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: getFieldBorderColor(theme),
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
  },
});

/**
 * Compact form field style used in dense workspaces such as Condition Studio.
 * Keeps inputs small while retaining the app-wide neutral outline.
 */
export const getCompactFormFieldSx = (theme: Theme): SxProps<Theme> => ({
  '& .MuiOutlinedInput-root': {
    minHeight: 36,
    fontSize: '0.875rem',
    borderRadius: `${borderRadius.sm}px`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.96 : 0.32),
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: getControlBorderColor(theme),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: getControlBorderColor(theme),
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: getControlBorderColor(theme),
      borderWidth: 1,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.875rem',
  },
});
